import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { api, TOKEN_KEY, refreshAccessToken } from '../api/client.js'

const AuthContext = createContext(null)
const USER_KEY = 'standalone.user'

function decodeUser(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return { userId: payload.userId, tenantId: payload.tenantId, passwordChangeRequired: Boolean(payload.passwordChangeRequired) }
  } catch {
    return null
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null)
  const [user, setUser] = useState(() => {
    const t = localStorage.getItem(TOKEN_KEY)
    if (!t) return null
    try {
      return JSON.parse(localStorage.getItem(USER_KEY)) || decodeUser(t)
    } catch {
      return decodeUser(t)
    }
  })
  const [checkingSession, setCheckingSession] = useState(Boolean(token))

  const applyToken = useCallback((t, u) => {
    localStorage.setItem(TOKEN_KEY, t)
    const next = u || decodeUser(t)
    localStorage.setItem(USER_KEY, JSON.stringify(next))
    setToken(t)
    setUser(next)
  }, [])

  useEffect(() => {
    const refreshed = e => applyToken(e.detail.token, e.detail.user)
    window.addEventListener('standalone:session-refreshed', refreshed)
    return () => window.removeEventListener('standalone:session-refreshed', refreshed)
  }, [applyToken])

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return
    if (window.location.pathname.startsWith('/login')) {
      setCheckingSession(false)
      return
    }
    let active = true
    refreshAccessToken()
      .then(() => {
        if (active) setCheckingSession(false)
      })
      .catch(() => {
        if (!active) return
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
        setToken(null)
        setUser(null)
        setCheckingSession(false)
      })
    return () => {
      active = false
    }
  }, [])

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post('/api/auth/login', { email, password })
      applyToken(data.token, data.user)
      return data.user
    },
    [applyToken]
  )

  const register = useCallback(
    async (email, password, displayName) => {
      const { data } = await api.post('/api/auth/register', { email, password, displayName })
      applyToken(data.token, data.user)
      return data.user
    },
    [applyToken]
  )

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      const { data } = await api.post('/api/auth/change-password', { currentPassword, newPassword })
      applyToken(data.token, data.user)
    },
    [applyToken]
  )

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    setToken(null)
    setUser(null)
    api.post('/api/auth/logout').catch(() => {})
  }, [])

  return (
    <AuthContext.Provider value={{ token, user, isLoggedIn: !!token, checkingSession, login, register, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
